import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { editClass } from "formValidations/classification";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import {
  useGetAllClassificationQuery,
  useUpdateClassificationMutation,
} from "store/apis/classifications";
import { BackendError } from "types/utils/error";
import { ClassificationWarningModal } from "./ClassificationWarningModal";

type EditClassificationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  className: string;
  classId: string;
  groupName: string;
};

export const EditClassificationModal = ({
  isOpen,
  onClose,
  className: classificationName,
  classId,
  groupName,
}: EditClassificationModalProps) => {
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const warningModal = useModal();
  const [updateClassification, { isLoading }] =
    useUpdateClassificationMutation();
  const { data: classifications = [] } = useGetAllClassificationQuery(
    { entityId },
    { skip: !entityId }
  );

  const handleSave = async (values: {
    groupName: string;
    className: string;
  }) => {
    if (values.className === classificationName) {
      onClose();
      return;
    }
    warningModal.open();
  };

  const handleConfirmUpdate = async (newName: string) => {
    try {
      await updateClassification({
        entityId,
        payload: { class_id: classId, name: newName },
      }).unwrap();

      successToast({ message: `Class renamed to "${newName}"` });
      warningModal.close();
      onClose();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content asChild>
        <FormikForm
          initialValues={{ groupName, className: classificationName }}
          onSubmit={handleSave}
          validationSchema={() =>
            editClass(classifications, groupName, classificationName)
          }
          validateOnChange
        >
          {({ values, submitForm }) => (
            <>
              <Modal.Header>
                <Modal.Title>Edit class</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-flex-col t-gap-5">
                <TextInput
                  name="groupName"
                  label="Group name"
                  placeholder="Group name"
                  required
                  disabled
                />
                <TextInput
                  name="className"
                  label="Class"
                  placeholder="Enter class name"
                  required
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button>Cancel</Button>
                </Modal.RawClose>
                <Button customType="primary" onClick={submitForm} type="button">
                  Save
                </Button>
              </Modal.FooterButtonGroup>
              {warningModal.isOpen && (
                <ClassificationWarningModal
                  isOpen={warningModal.isOpen}
                  onClose={warningModal.close}
                  onConfirm={() => handleConfirmUpdate(values.className)}
                  title="Update classification name"
                  description="Renaming the classification will apply the change to all transactions assigned to it. Please confirm to proceed."
                  isLoading={isLoading}
                />
              )}
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
